import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { KeyRound, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { useResetPasswordMutation, useResendOtpMutation } from '../../store/api/authApi';
import AuthShell, { AuthField, AuthSubmit, AuthAlert } from './AuthShell';
import OTPInput from '../common/OTPInput';
import useTimer from '../../hooks/useTimer';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();

  const [email, setEmail] = useState(location.state?.email || '');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(email ? 2 : 1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { isActive, reset, formatTime } = useTimer(60);

  useEffect(() => {
    if (!email && step > 1) {
      setStep(1);
    }
  }, [email, step]);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    setStep(2);
  };

  const handleOTPComplete = (code) => {
    setOtp(code);
  };

  const handleResendOTP = async () => {
    setError('');

    try {
      await resendOtp({ email, type: 'forgot_password' }).unwrap();
      reset();
    } catch (err) {
      setError(err.data?.message || 'Failed to resend OTP');
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    if (!newPassword) {
      setError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setError('Password must contain uppercase, lowercase, and number');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await resetPassword({ email, otp, newPassword }).unwrap();
      setSuccess(true);
    } catch (err) {
      setError(err.data?.message || 'Failed to reset password. Please try again.');
    }
  };

  if (success) {
    return (
      <AuthShell
        width="narrow"
        icon={<CheckCircle2 size={20} />}
        eyebrow="Done"
        title="Password changed"
        subtitle="You can now sign in with your new password."
      >
        <button
          type="button"
          className="mp-auth-btn is-primary is-block"
          onClick={() => navigate('/login')}
        >
          Continue to sign in
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      width="narrow"
      icon={<KeyRound size={20} />}
      eyebrow={`Step ${step} of 2`}
      title="Reset your password"
      subtitle={
        step === 1
          ? 'Enter the email on your account to continue.'
          : <>Enter the code sent to <strong>{email}</strong> and choose a new password.</>
      }
    >
      {error && <AuthAlert>{error}</AuthAlert>}

      {step === 1 ? (
        <form onSubmit={handleEmailSubmit} noValidate>
          <div className="mp-auth-fields">
            <AuthField
              id="email"
              type="email"
              label="Work email"
              placeholder="you@company.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div style={{ marginTop: 22 }}>
            <AuthSubmit label="Continue" />
          </div>
          <p className="mp-auth-foot">
            <Link to="/login">Back to sign in</Link>
          </p>
        </form>
      ) : (
        <form onSubmit={handleResetSubmit} noValidate>
          <label className="mp-auth-label" htmlFor="reset-otp">Reset code</label>
          <OTPInput onComplete={handleOTPComplete} />

          <p className="mp-auth-resend">
            {isActive ? (
              <>Did not receive it? Resend in <span className="mp-auth-timer">{formatTime()}</span></>
            ) : (
              <>
                Did not receive it?{' '}
                <button
                  type="button"
                  className="mp-auth-link"
                  onClick={handleResendOTP}
                  disabled={isResending}
                >
                  {isResending ? 'Sending…' : 'Resend code'}
                </button>
              </>
            )}
          </p>

          <div className="mp-auth-fields" style={{ marginTop: 22 }}>
            <AuthField
              id="newPassword"
              type={showPassword ? 'text' : 'password'}
              label="New password"
              placeholder="••••••••"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              hint="At least 8 characters, with an uppercase letter, a lowercase letter and a number."
              trailing={
                <button
                  type="button"
                  className="mp-auth-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              }
            />

            <AuthField
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm new password"
              placeholder="••••••••"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              trailing={
                <button
                  type="button"
                  className="mp-auth-eye"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              }
            />
          </div>

          <div style={{ marginTop: 22 }}>
            <AuthSubmit label="Reset password" loading={isLoading} />
          </div>

          <p className="mp-auth-foot">
            <Link to="/login">Back to sign in</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
};

export default ResetPassword;
